/**
 * PdfPreview — renders a PDF byte stream from an authenticated backend URL
 * into scrollable canvas pages using pdfjs-dist. Byte-identical to the
 * exported PDF because it re-uses the same bytes from
 * ``/api/books/{id}/preview/pdf``.
 *
 * We avoid ``<iframe src="…pdf">`` because iOS Safari and headless Chromium
 * do not render inline PDFs reliably; pdf.js on canvas works everywhere.
 *
 * Pages are laid out as placeholder divs first (correct dimensions), then
 * rendered lazily via IntersectionObserver as they scroll near the viewport.
 * That makes even 400-page books load instantly.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { api } from "@/lib/api";

// Worker script version must match the installed pdfjs-dist version.
pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

// Render pages this far outside the viewport so scrolling feels instant.
const IO_ROOT_MARGIN = "600px 0px";

export function PdfPreview({ url, cacheKey, onLoaded, className = "" }) {
  const containerRef = useRef(null);
  const pdfRef = useRef(null);
  const observerRef = useRef(null);
  const [pageCount, setPageCount] = useState(0);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [error, setError] = useState(null);
  const runIdRef = useRef(0);

  const buildPlaceholders = useCallback(async (pdf) => {
    const container = containerRef.current;
    if (!container) return;
    container.innerHTML = "";
    const targetWidth = Math.max(320, Math.min(container.clientWidth || 720, 1000)) - 24;
    // Use the first page's viewport as a reference for placeholder sizing.
    // Most books use a single trim size so this is representative.
    const p1 = await pdf.getPage(1);
    const baseVp = p1.getViewport({ scale: 1 });
    const scale = targetWidth / baseVp.width;
    const placeholderH = Math.floor(baseVp.height * scale);
    const placeholderW = Math.floor(baseVp.width * scale);

    for (let n = 1; n <= pdf.numPages; n++) {
      const wrap = document.createElement("div");
      wrap.className = "pdf-page-wrap";
      wrap.style.textAlign = "center";
      wrap.style.padding = "8px 12px";
      wrap.setAttribute("data-page", String(n));
      wrap.setAttribute("data-rendered", "false");

      const caption = document.createElement("p");
      caption.textContent = `Page ${n} of ${pdf.numPages}`;
      caption.style.fontSize = "11px";
      caption.style.letterSpacing = "0.15em";
      caption.style.textTransform = "uppercase";
      caption.style.color = "#8a8478";
      caption.style.margin = "10px 0 4px 0";
      wrap.appendChild(caption);

      const placeholder = document.createElement("div");
      placeholder.className = "pdf-page-placeholder";
      placeholder.style.width = `${placeholderW}px`;
      placeholder.style.height = `${placeholderH}px`;
      placeholder.style.margin = "0 auto 16px auto";
      placeholder.style.background = "#fdfcf9";
      placeholder.style.boxShadow = "0 1px 2px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.06)";
      placeholder.style.display = "flex";
      placeholder.style.alignItems = "center";
      placeholder.style.justifyContent = "center";
      placeholder.style.color = "#c9c3b6";
      placeholder.style.fontSize = "11px";
      placeholder.style.letterSpacing = "0.2em";
      placeholder.textContent = "…";
      wrap.appendChild(placeholder);
      container.appendChild(wrap);
    }
    return { targetWidth };
  }, []);

  const renderPage = useCallback(async (n, targetWidth) => {
    const pdf = pdfRef.current;
    if (!pdf) return;
    const container = containerRef.current;
    const wrap = container?.querySelector(`[data-page="${n}"]`);
    if (!wrap || wrap.getAttribute("data-rendered") === "true") return;
    wrap.setAttribute("data-rendered", "true"); // guard against double-render

    try {
      const page = await pdf.getPage(n);
      const baseVp = page.getViewport({ scale: 1 });
      const scale = targetWidth / baseVp.width;
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement("canvas");
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      canvas.style.display = "block";
      canvas.style.margin = "0 auto 16px auto";
      canvas.style.background = "white";
      canvas.style.boxShadow = "0 1px 2px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.06)";

      const ctx = canvas.getContext("2d");
      await page.render({ canvasContext: ctx, viewport }).promise;

      const placeholder = wrap.querySelector(".pdf-page-placeholder");
      if (placeholder) wrap.replaceChild(canvas, placeholder);
    } catch (err) {
      // Non-fatal per page; keep placeholder visible with an error tag.
      wrap.setAttribute("data-rendered", "error");
      console.warn(`PdfPreview page ${n} error:`, err?.message || err);
    }
  }, []);

  const setupObserver = useCallback((targetWidth) => {
    if (observerRef.current) observerRef.current.disconnect();
    const container = containerRef.current;
    if (!container) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const el = entry.target;
            const n = Number(el.getAttribute("data-page"));
            if (n) renderPage(n, targetWidth);
            io.unobserve(el);
          }
        }
      },
      { root: container, rootMargin: IO_ROOT_MARGIN, threshold: 0.01 }
    );
    container.querySelectorAll("[data-page]").forEach((el) => io.observe(el));
    observerRef.current = io;
  }, [renderPage]);

  const load = useCallback(async () => {
    const myRun = ++runIdRef.current;
    setStatus("loading");
    setError(null);

    try {
      const { data } = await api.get(url.replace(/^.*\/api/, ""), { responseType: "arraybuffer" });
      if (myRun !== runIdRef.current) return;

      // Destroy any previously loaded PDF before replacing it.
      try { pdfRef.current?.destroy(); } catch { /* noop */ }
      pdfRef.current = null;

      const loadingTask = pdfjsLib.getDocument({
        data,
        cMapUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/cmaps/`,
        cMapPacked: true,
        standardFontDataUrl:
          `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/standard_fonts/`,
      });
      const pdf = await loadingTask.promise;
      if (myRun !== runIdRef.current) { try { pdf.destroy(); } catch { /* noop */ } return; }
      pdfRef.current = pdf;
      setPageCount(pdf.numPages);

      const { targetWidth } = await buildPlaceholders(pdf);
      // Render the first two pages immediately so the preview isn't blank.
      await renderPage(1, targetWidth);
      if (pdf.numPages >= 2) renderPage(2, targetWidth);
      setupObserver(targetWidth);
      setStatus("ready");
      onLoaded?.();
    } catch (err) {
      if (myRun !== runIdRef.current) return;
      console.error("PdfPreview error:", err);
      setError(err?.response?.data?.detail || err?.message || "Failed to load preview");
      setStatus("error");
      onLoaded?.();
    }
  }, [url, buildPlaceholders, renderPage, setupObserver, onLoaded]);

  useEffect(() => {
    load();
    return () => {
      runIdRef.current += 1;
      if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
      try { pdfRef.current?.destroy(); } catch { /* noop */ }
      pdfRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, cacheKey]);

  return (
    <div className={className} data-testid="pdf-preview-root">
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/70 pointer-events-none z-10"
             data-testid="pdf-preview-loading">
          <p className="text-sm text-[color:var(--text-muted)] uppercase tracking-widest">
            Rendering preview…
          </p>
        </div>
      )}
      {status === "error" && (
        <div className="p-6 text-sm text-red-600" data-testid="pdf-preview-error">
          Preview failed to render: {error}
        </div>
      )}
      <div
        ref={containerRef}
        className="w-full h-full overflow-y-auto"
        data-testid="pdf-preview-pages"
        data-page-count={pageCount}
      />
    </div>
  );
}
